import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  TextField,
  InputAdornment,
  IconButton,
  Typography,
  alpha,
  CircularProgress,
  Grid,
  ImageListItem,
  Button,
} from '@mui/material';
import { Search, Close, Send } from '@mui/icons-material';
import { ClickAwayListener } from '@mui/material';
import chatApi from '../../services/chatApi';
import { Gif } from '../../types/Chat';

interface GifPickerProps {
  open: boolean;
  onClose: () => void;
  onSelect: (gifUrl: string) => void;
  isDark: boolean;
}

const GifPicker: React.FC<GifPickerProps> = ({ open, onClose, onSelect, isDark }) => {
  const [gifs, setGifs] = useState<Gif[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedGif, setSelectedGif] = useState<string | null>(null);
  const [selectedGifPreview, setSelectedGifPreview] = useState<string | null>(null);

  const goldColor = '#C4B66F';
  const deepGreen = '#051814';

  // Glass effect styling
  const glassEffect = {
    backdropFilter: 'blur(20px)',
    background: isDark
      ? 'linear-gradient(135deg, rgba(5, 24, 20, 0.98) 0%, rgba(15, 59, 46, 0.98) 100%)'
      : 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(250, 250, 250, 0.98) 100%)',
    border: `1px solid ${alpha(isDark ? goldColor : deepGreen, 0.2)}`,
    boxShadow: isDark
      ? `0 25px 50px ${alpha('#000000', 0.5)}`
      : `0 25px 50px ${alpha(deepGreen, 0.15)}`,
  };

  useEffect(() => {
    if (open) {
      fetchTrendingGifs();
      setSelectedGif(null);
    }
  }, [open]);

  const fetchTrendingGifs = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await chatApi.getTrendingGifs(30);
      if (response.success) {
        setGifs(response.gifs);
      }
    } catch (err: any) {
      console.error('Error fetching trending GIFs:', err);
      setError('Failed to load GIFs');
    } finally {
      setLoading(false);
    }
  };

  const searchGifs = async (query: string) => {
    if (!query.trim()) {
      fetchTrendingGifs();
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await chatApi.searchGifs({ q: query, limit: 30 });
      if (response.success) {
        setGifs(response.gifs);
      }
    } catch (err: any) {
      console.error('Error searching GIFs:', err);
      setError('Failed to search GIFs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (open) {
        searchGifs(search);
      }
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [search, open]);

  const handleGifClick = (gif: Gif) => {
    setSelectedGif(gif.url);
    setSelectedGifPreview(gif.preview);
  };

  const handleSendGif = () => {
    if (selectedGif) {
      onSelect(selectedGif);
      setSelectedGif(null);
      setSelectedGifPreview(null);
      onClose();
    }
  };

  if (!open) return null;

  return (
    <ClickAwayListener onClickAway={onClose}>
      <Paper
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: '100%',
          height: 500,
          ...glassEffect,
          borderRadius: '16px 16px 0 0',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          zIndex: 1400,
        }}
      >
        {/* Update Header to include close button */}
        <Box sx={{ 
          p: 2, 
          borderBottom: `1px solid ${alpha(isDark ? goldColor : deepGreen, 0.1)}`,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        }}>
          <IconButton
            size="small"
            onClick={onClose}
            sx={{ color: goldColor }}
          >
            <Close />
          </IconButton>
          <TextField
            fullWidth
            placeholder="Search GIFs..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                bgcolor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(5,24,20,0.02)',
                borderColor: alpha(isDark ? goldColor : deepGreen, 0.2),
                '&:hover': {
                  borderColor: goldColor,
                },
              },
              '& .MuiInputBase-input': {
                color: isDark ? '#fff' : deepGreen,
                fontSize: '0.9rem',
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ fontSize: 20, color: goldColor }} />
                </InputAdornment>
              ),
              endAdornment: search && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setSearch('')}
                    sx={{ color: goldColor }}
                  >
                    <Close fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Selected GIF Preview */}
        {selectedGif && (
          <Box sx={{ 
            p: 2, 
            borderBottom: `1px solid ${alpha(isDark ? goldColor : deepGreen, 0.1)}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            bgcolor: alpha(goldColor, 0.05),
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ width: 60, height: 60, borderRadius: '8px', overflow: 'hidden' }}>
                <img
                  src={selectedGifPreview || selectedGif}
                  alt="Selected GIF"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </Box>
              <Typography variant="body2" sx={{ color: isDark ? '#fff' : deepGreen }}>
                GIF selected
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setSelectedGif(null)}
                sx={{
                  color: isDark ? alpha('#fff', 0.7) : alpha(deepGreen, 0.7),
                  borderColor: alpha(isDark ? goldColor : deepGreen, 0.3),
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={handleSendGif}
                endIcon={<Send />}
                sx={{
                  bgcolor: goldColor,
                  color: deepGreen,
                  '&:hover': {
                    bgcolor: alpha(goldColor, 0.8),
                  },
                }}
              >
                Send
              </Button>
            </Box>
          </Box>
        )}

        {/* GIF Grid - Two Column Layout */}
        <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <CircularProgress sx={{ color: goldColor }} />
            </Box>
          ) : error ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography sx={{ color: isDark ? alpha('#fff', 0.7) : alpha(deepGreen, 0.7) }}>
                {error}
              </Typography>
            </Box>
          ) : gifs.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography sx={{ color: isDark ? alpha('#fff', 0.7) : alpha(deepGreen, 0.7) }}>
                No GIFs found
              </Typography>
            </Box>
          ) : (
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(2, 1fr)', // Two columns
              gap: 1.5,
              width: '100%',
            }}>
              {gifs.map((gif) => (
                <Box
                  key={gif.id}
                  sx={{
                    width: '100%',
                    aspectRatio: '1.5', // Maintain consistent aspect ratio
                    borderRadius: '8px',
                    overflow: 'hidden',
                    cursor: 'pointer',
                    border: selectedGif === gif.url ? `2px solid ${goldColor}` : `1px solid ${alpha(isDark ? goldColor : deepGreen, 0.1)}`,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      transform: 'scale(1.02)',
                      borderColor: goldColor,
                      boxShadow: `0 4px 12px ${alpha(goldColor, 0.3)}`,
                    },
                  }}
                  onClick={() => handleGifClick(gif)}
                >
                  <img
                    src={gif.preview}
                    alt={gif.title || 'GIF'}
                    loading="lazy"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover', // Ensures GIF fills the container
                    }}
                  />
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Paper>
    </ClickAwayListener>
  );
};

export default GifPicker;